import classNames from "classnames";
import { Field, useField } from "formik";

import { Clinic, UserName } from "pwapi";

import { getBillingProperties } from "shared/utils/caseUtils";
import { getZoneString } from "shared/utils/dates";
import {
  getBillingTypeOptions,
  isNoBillingConstrainedCaseType,
} from "shared/utils/rulesEngineUtils";
import {
  getValueFromObject,
  requiredError,
} from "shared/utils/validationUtils";

import { useAppSelector } from "app/hooks";
import { useAllowsExternalAccession } from "hooks/useAllowsExternalAccession";
import { useSelectedLab } from "hooks/useSelectedLabStation";
import { Input } from "ui/accessioning/AccessionForm";
import useCaseInputs from "ui/CaseFormInputs/useCaseInputs";

import FormHeaders from "./FormHeaders";

export const getUserName = (u: UserName) => `${u.first_name} ${u.last_name}`;

const CaseDetailsForm = ({
  onHandlePatchAccession,
  editingAccession,
  setValues,
  values,
  displaySubmit = false,
  dirty = false,
  onHandleDiscardChanges,
  field_meta,
  remains_digital_pc,
  errors = {},
  touched = {},
  warnings = {},
  validateForm,
}: {
  onHandlePatchAccession?: Function;
  editingAccession?: any;
  setValues: Function;
  displaySubmit?: boolean;
  dirty?: boolean;
  onHandleDiscardChanges?: Function;
  validateForm?: Function;
  values: any;
  field_meta?: {
    billing_type?: { value?: string; disabled?: boolean };
    bill_tc?: { value?: boolean; disabled?: boolean };
    bill_pc?: { value?: boolean; disabled?: boolean };
  } | null;
  remains_digital_pc?: boolean;
  errors?: any;
  touched?: any;
  warnings?: { [key: string]: string };
}) => {
  const caseInputs = useCaseInputs();

  const lab = useSelectedLab();

  const clinics = useAppSelector((state) => state.clinics);

  const externalAccessionRequired = useAllowsExternalAccession({
    clinic: values.clinic as Clinic,
    grossed_by_external_lab_id: values.grossed_by_external_lab_id as number,
    accessioning_lab: lab,
    case_type: values.case_type || values.type,
    remains_digital_pc,
    bill_tc: values.bill_tc,
    bill_pc: values.bill_pc,
  });
  // eslint-disable-next-line
  const [field, meta, { setTouched }] = useField("billing_type");
  const billingTypeError = getValueFromObject(errors, "billing_type");
  const isConstrainedBillingCaseType = isNoBillingConstrainedCaseType(
    values.case_type
  );
  const billingComponentsDisabled =
    isConstrainedBillingCaseType ||
    (field_meta?.bill_tc?.disabled && field_meta?.bill_pc?.disabled);

  return (
    <>
      <FormHeaders
        title="Case and Billing Details"
        displaySubmit={displaySubmit}
        dirty={dirty}
        onHandleDiscardChanges={onHandleDiscardChanges}
        errors={errors}
        touched={touched}
        warnings={warnings}
        forms={[
          "billing_type",
          "collected_date",
          "collected_date",
          "received_date",
        ]}
        formWarnings={["collected_date"]}
      />
      <div className="LISForm__section">
        <Input
          {...caseInputs.collectedDate}
          type="datetime-local"
          className="Input--3"
          handleOnBlur={
            onHandlePatchAccession &&
            ((value: string) => {
              value &&
                onHandlePatchAccession({
                  collected_date: value,
                });
            })
          }
          labelDetails={
            <>
              (
              {getZoneString(
                editingAccession?.collected_date_timezone ||
                  values?.clinic?.location?.timezone
              )}
              )
            </>
          }
          min="2000-01-01T00:00"
          required
          error={getValueFromObject(errors, "collected_date")}
          warning={getValueFromObject(warnings, "collected_date")}
          touched={getValueFromObject(touched, "collected_date")}
        />
        <Input
          {...caseInputs.receivedDate}
          type="datetime-local"
          className="Input--3"
          handleOnBlur={
            onHandlePatchAccession &&
            ((value: string) => {
              value &&
                onHandlePatchAccession({
                  received_date: value,
                });
            })
          }
          labelDetails={
            <>
              (
              {getZoneString(
                editingAccession?.received_date_timezone ||
                  lab?.location?.timezone
              )}
              )
            </>
          }
          required
          error={getValueFromObject(errors, "received_date")}
          touched={getValueFromObject(touched, "received_date")}
        />
        <div
          className={classNames(
            "Accession__input",
            "Accession__input--required",
            "Input--1",
            billingTypeError &&
              getValueFromObject(touched, "billing_type") &&
              !requiredError(billingTypeError) &&
              "Accession__input--error",
            billingTypeError && "Accession__input--submission-check-error"
          )}
        >
          <label htmlFor="case__billing_type">
            {caseInputs.billingType.label} *
          </label>
          <Field
            {...caseInputs.billingType}
            name="billing_type"
            as="select"
            onBlur={(e: Event) => {
              setTouched(true);
              onHandlePatchAccession &&
                onHandlePatchAccession({
                  billing_type: (e.target as HTMLInputElement).value || null,
                });
            }}
            required
            disabled={
              field_meta?.billing_type ||
              undefined ||
              isConstrainedBillingCaseType
            }
          >
            <option key="" value="" disabled>
              Select billing type...
            </option>
            {getBillingTypeOptions(
              clinics?.[values.clinic?.id || -1]?.billing_types || [],
              values.case_type
            ).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Field>
        </div>
        <div className={classNames("Accession__input", "Input--1")}>
          <label htmlFor="case__billing_properties">Self Pay / Indigent</label>
          <Field
            id="case__billing_properties"
            name="billing_properties"
            as="select"
            disabled={isConstrainedBillingCaseType}
            value={
              values.indigent
                ? "indigent"
                : values.self_pay_with_attestation
                ? "self_pay_with_attestation"
                : values.self_pay
                ? "self_pay"
                : ""
            }
            onChange={(e: any) => {
              setValues({
                ...values,
                ...getBillingProperties(e.target.value || null),
              });
            }}
            onBlur={(e: Event) => {
              const value = (e.target as HTMLInputElement).value || null;

              onHandlePatchAccession &&
                onHandlePatchAccession({
                  ...getBillingProperties(value),
                });
            }}
          >
            <option value="">Not Applicable</option>
            {caseInputs.billingProperties
              .filter(
                (option) =>
                  option.value !== "indigent" ||
                  lab?.settings?.auto_print_indigent_agreement_notice?.active
              )
              .map((option) => (
                <option key={option.name} value={option.value}>
                  {option.label}
                </option>
              ))}
          </Field>
        </div>
        <fieldset className="Accession__radio-fieldset Input--2">
          <legend className="Accession__radio-legend">Billing Component</legend>
          <Input
            {...caseInputs.billingComponent.tcOnly}
            checked={values.bill_tc && !values.bill_pc}
            disabled={billingComponentsDisabled}
            className="Accession__radio-item"
            handleOnClick={() => {
              setValues({
                ...values,
                bill_tc: true,
                bill_pc: !values.bill_pc,
              }).then(() => {
                validateForm && validateForm();
              });

              onHandlePatchAccession &&
                onHandlePatchAccession(
                  !values.bill_pc
                    ? { bill_pc: true, bill_tc: true }
                    : { bill_pc: false, bill_tc: true }
                );
            }}
          />
          <Input
            {...caseInputs.billingComponent.pcOnly}
            checked={values.bill_pc && !values.bill_tc}
            disabled={billingComponentsDisabled}
            className="Accession__radio-item"
            handleOnClick={() => {
              setValues({
                ...values,
                bill_pc: true,
                bill_tc: !values.bill_tc,
              });
              onHandlePatchAccession &&
                onHandlePatchAccession(
                  !values.bill_tc
                    ? { bill_pc: true, bill_tc: true }
                    : { bill_pc: true, bill_tc: false }
                );
            }}
          />
        </fieldset>
        <Input
          {...caseInputs.externalAccession}
          className="Input--2"
          handleOnBlur={
            onHandlePatchAccession &&
            ((value: string) => {
              onHandlePatchAccession({ external_accession: value });
            })
          }
          disabled={!externalAccessionRequired}
          required={externalAccessionRequired}
        />
      </div>
    </>
  );
};

export default CaseDetailsForm;
